import { Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

import { ErrorState, FeatureErrorBoundary } from '@nucleus-commerce/admin-kit/errors';
import { StatCard } from '@nucleus-commerce/admin-kit/layout';
import { useFormat } from '@nucleus-commerce/i18n/react';
import { Card, CardHeader, CardTitle } from '@nucleus-commerce/ui/components/card';
import { Skeleton } from '@nucleus-commerce/ui/components/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@nucleus-commerce/ui/components/table';

import { inventoryListSearchSchema } from '@/features/inventory';
import { useStore } from '@/shared/store/store-context';

import { ReportExportButton } from '../components/report-export-button';
import { useInventoryReport } from '../hooks/use-inventory-report';

/** A snapshot of stock right now; it takes no range. */
function InventoryReport() {
  const { t } = useTranslation('reports');
  const store = useStore();
  const format = useFormat();
  const query = useInventoryReport(store.id);
  const report = query.data;

  if (query.isError) {
    return <ErrorState error={query.error} onRetry={() => void query.refetch()} />;
  }

  const stat = (key: string, value: string | undefined, tone?: 'warning') => (
    <div role="group" aria-label={t(`inventory.${key}`)} className="contents">
      <StatCard
        label={t(`inventory.${key}`)}
        value={value ?? '—'}
        tone={tone}
        isLoading={query.isPending}
      />
    </div>
  );

  return (
    <div className="flex flex-col gap-4">
      <div className="flex justify-end">
        <ReportExportButton storeId={store.id} report="inventory" />
      </div>
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-5" data-tour="reports.inventory.tiles">
        {stat('onHand', report && format.number(report.onHandUnits))}
        {stat('reserved', report && format.number(report.reservedUnits))}
        {stat('stockValue', report && format.money(report.stockValue))}
        {stat('lowStock', report && format.number(report.lowStockCount))}
        {stat('outOfStock', report && format.number(report.outOfStockCount))}
      </div>
      <Card className="gap-0 overflow-hidden py-0" data-tour="reports.inventory.table">
        <CardHeader className="border-b px-5 py-4">
          <CardTitle className="text-[15px]">{t('inventory.lowStockTitle')}</CardTitle>
          {report?.truncated ? (
            <p className="text-muted-foreground text-xs">
              {t('lowStockTruncated', {
                count: report.limit,
                total: report.lowStockCount + report.outOfStockCount,
              })}
            </p>
          ) : null}
        </CardHeader>
        <Table aria-label={t('inventory.lowStockTitle')}>
          <TableHeader>
            <TableRow>
              <TableHead className="pl-5">{t('columns.product')}</TableHead>
              <TableHead className="hidden md:table-cell">{t('columns.sku')}</TableHead>
              <TableHead>{t('columns.location')}</TableHead>
              <TableHead className="text-right">{t('columns.available')}</TableHead>
              <TableHead className="hidden text-right md:table-cell">
                {t('columns.reserved')}
              </TableHead>
              <TableHead className="pr-5 text-right">{t('columns.reorderPoint')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {query.isPending ? (
              Array.from({ length: 4 }).map((_, i) => (
                <TableRow key={i}>
                  <TableCell colSpan={6} className="px-5">
                    <Skeleton className="h-5" />
                  </TableCell>
                </TableRow>
              ))
            ) : report && report.lowStock.length > 0 ? (
              report.lowStock.map((row) => (
                <TableRow key={`${row.locationId}:${row.variantId}`}>
                  <TableCell className="pl-5">
                    <span className="font-medium">{row.productTitle}</span>
                    <span className="text-muted-foreground block text-xs">{row.variantTitle}</span>
                  </TableCell>
                  <TableCell className="text-muted-foreground hidden font-mono text-xs md:table-cell">
                    {row.sku ?? '—'}
                  </TableCell>
                  <TableCell>
                    <Link
                      to="/s/$storeId/inventory"
                      params={{ storeId: store.id }}
                      search={inventoryListSearchSchema.parse({
                        locationId: row.locationId,
                        lowStock: true,
                      })}
                      className="hover:underline"
                    >
                      {row.locationName}
                    </Link>
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {format.number(row.available)}
                  </TableCell>
                  <TableCell className="hidden text-right tabular-nums md:table-cell">
                    {format.number(row.reserved)}
                  </TableCell>
                  <TableCell className="pr-5 text-right tabular-nums">
                    {row.reorderPoint === null ? '—' : format.number(row.reorderPoint)}
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={6} className="text-muted-foreground h-24 text-center">
                  {t('empty.noLowStock')}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}

export function InventoryReportPage() {
  return (
    <FeatureErrorBoundary name="reports.inventory">
      <InventoryReport />
    </FeatureErrorBoundary>
  );
}
